import type { CSSProperties } from 'react'
import { fixedDecimals } from '../fixedDecimals'
import './meter.css'

/** The track is a percentage of itself; a name, not a literal, so a geometry ratio is not read as money arithmetic. */
const WHOLE = 100

export type MeterSkin = 'neutral' | 'spent' | 'warn' | 'over' | 'onAccent'

/**
 * The one meter: `role="meter"`, a track and a fill sized through `--pct`.
 *
 * - `value` is the TRUTHFUL figure (`aria-valuenow`, never clamped); `max`
 *   defaults to 100 and `aria-valuemax` never trails the value (a meter whose
 *   max is under its now is invalid); `aria-valuemin` likewise never leads it
 *   (a credit balance is a negative percentage). The fill is clamped to the track.
 * - `fill` is the value the FILL draws when it differs from the announced one
 *   (a server percentage to one decimal beside a rounded `value`); it defaults
 *   to `value`.
 * - The skin is a CLASS; every colour, outline and radius is in `meter.css`
 *   (rule 2). `neutral` is the recessed groove, `spent`/`warn`/`over` the
 *   envelope fills, `onAccent` the track-and-fill pair for the lime tile.
 *   State is also said in `valueText`: colour is never the only signal.
 * - `className` is for the caller's LAYOUT only (flex, margin, grid placement).
 */
export function Meter({
  value,
  max = 100,
  fill = value,
  skin = 'neutral',
  orientation = 'bar',
  compact = false,
  label,
  valueText,
  className,
}: {
  value: number
  max?: number
  fill?: number
  skin?: MeterSkin
  orientation?: 'bar' | 'column'
  compact?: boolean
  label: string
  valueText?: string
  className?: string
}) {
  const pct = max > 0 ? Math.min(Math.max((fill / max) * WHOLE, 0), WHOLE) : 0
  const cls = ['meter', `meter--${orientation}`, `meter--${skin}`, compact && 'meter--compact', className]
    .filter(Boolean)
    .join(' ')
  return (
    <span
      role="meter"
      aria-label={label}
      aria-valuemin={Math.min(0, value)}
      aria-valuemax={Math.max(max, value)}
      aria-valuenow={value}
      aria-valuetext={valueText}
      className={cls}
    >
      <span
        className="meter__fill"
        {...(orientation === 'column' ? { 'data-chart': '' } : {})}
        style={{ '--pct': fixedDecimals(pct, 1) } as CSSProperties}
      />
    </span>
  )
}
